import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { Plus, Download, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./Input";
import ConfirmDialog from "@/components/ConfirmDialog";

export default function EmployeesTab() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", name: "", phone: "", email: "", basic_salary: "", monthly_target: "", full_day_cutoff_time: "10:30" });
  const [bulkResult, setBulkResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const filteredItems = useMemo(() => items.filter((employee) => {
    if (statusFilter === "active" && !employee.active) return false;
    if (statusFilter === "inactive" && employee.active) return false;
    const query = search.trim().toLowerCase();
    return !query || [employee.name, employee.username, employee.phone, employee.email].some((value) => String(value || "").toLowerCase().includes(query));
  }), [items, search, statusFilter]);

  const load = () => api.get("/admin/employees?include_inactive=true").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/employees", { ...form, basic_salary: parseFloat(form.basic_salary||0), monthly_target: parseFloat(form.monthly_target||0) });
      toast.success("Employee added");
      setForm({ username: "", password: "", name: "", phone: "", email: "", basic_salary: "", monthly_target: "", full_day_cutoff_time: "10:30" });
      setCreating(false);
      load();
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
  };

  const permDelete = async (emp) => {
    try {
      await api.delete(`/admin/employees/${emp.id}?permanent=true`);
      toast.success("Deleted");
      load();
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
  };

  const toggleActive = async (emp) => {
    try {
      await api.patch(`/admin/employees/${emp.id}`, { active: !emp.active });
      load();
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
  };

  const resetPassword = async (emp) => {
    const pw = newPassword;
    if (!pw) return;
    if (pw.length < 6) { toast.error("Password too short (min 6 characters)"); return; }
    try {
      await api.patch(`/admin/employees/${emp.id}`, { password: pw });
      toast.success(`Password reset for ${emp.username}`);
      setPasswordTarget(null);
      setNewPassword("");
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setSavingEdit(true);
    try {
      await api.patch(`/admin/employees/${editing.id}`, {
        name: editing.name,
        phone: editing.phone || null,
        email: editing.email || null,
        basic_salary: parseFloat(editing.basic_salary || 0),
        monthly_target: parseFloat(editing.monthly_target || 0),
        full_day_cutoff_time: editing.full_day_cutoff_time || "10:30",
      });
      toast.success("Employee updated");
      setEditing(null);
      load();
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
    finally { setSavingEdit(false); }
  };

  const downloadTemplate = () => {
    fetch(`${API}/admin/employees/bulk/template`, { credentials: "include" })
      .then(r => r.blob()).then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "employees_template.csv";
        a.click();
      });
  };

  const bulkImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/employees/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkResult(data);
      toast.success(`Imported ${data.created_count} · Skipped ${data.skipped_count}`);
      load();
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
    finally { setImporting(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-employees">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-overline">Team</div>
          <h2 className="text-2xl font-light text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Employees ({filteredItems.length})</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="download-employee-template" onClick={downloadTemplate}
            className="border border-[#EAE6DF] hover:border-[#4A5D4E] rounded-xl px-4 py-2 text-sm text-[#2D312E] flex items-center gap-2">
            <Download className="h-4 w-4" /> CSV template
          </button>
          <label data-testid="bulk-import-label" className={`border border-[#EAE6DF] hover:border-[#4A5D4E] rounded-xl px-4 py-2 text-sm text-[#2D312E] flex items-center gap-2 cursor-pointer ${importing ? "opacity-60" : ""}`}>
            <Plus className="h-4 w-4" /> {importing ? "Importing…" : "Bulk import CSV"}
            <input data-testid="bulk-import-input" type="file" accept=".csv" onChange={bulkImport} className="hidden" disabled={importing} />
          </label>
          <button
            data-testid="add-employee-button"
            onClick={() => setCreating((v) => !v)}
            className="bg-[#4A5D4E] hover:bg-[#3B4A3E] text-white rounded-xl px-4 py-2 text-sm flex items-center gap-2"
          >
            <Plus className="h-4 w-4" /> Add employee
          </button>
        </div>
      </div>

      {bulkResult && (
        <div className="card-warm p-4 text-sm" data-testid="bulk-import-result">
          <div className="text-[#2D312E] font-medium mb-2">
            Bulk import: <span className="text-emerald-700">{bulkResult.created_count} created</span> · <span className="text-[#C05746]">{bulkResult.skipped_count} skipped</span>
          </div>
          {bulkResult.skipped?.length > 0 && (
            <div className="text-xs text-[#5C635E] max-h-48 overflow-auto">
              <div className="font-semibold text-[#5C635E] mb-1">Skipped rows:</div>
              {bulkResult.skipped.map((s, i) => (
                <div key={i} data-testid={`bulk-skip-${i}`}>Row {s.row}: <span className="font-mono">{s.username || "—"}</span> — {s.reason}</div>
              ))}
            </div>
          )}
          <button onClick={() => setBulkResult(null)} className="mt-2 text-xs text-[#8A938D] hover:underline">Dismiss</button>
        </div>
      )}

      {creating && (
        <form onSubmit={create} className="card-warm p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="add-employee-form">
          <Input label="Username*" testid="new-employee-username" value={form.username} onChange={(v) => setForm({ ...form, username: v })} />
          <Input label="Password*" testid="new-employee-password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} />
          <Input label="Full name*" testid="new-employee-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Input label="Phone" testid="new-employee-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
          <Input label="Email" testid="new-employee-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
          <Input label="Basic Salary (₹/month)" testid="new-employee-basic-salary" type="number" value={form.basic_salary} onChange={(v) => setForm({ ...form, basic_salary: v })} />
          <Input label="Monthly Target (₹)" testid="new-employee-target" type="number" value={form.monthly_target} onChange={(v) => setForm({ ...form, monthly_target: v })} />
          <Input label="Full Day Cutoff" testid="new-employee-cutoff" type="time" value={form.full_day_cutoff_time} onChange={(v) => setForm({ ...form, full_day_cutoff_time: v })} />
          <div className="md:col-span-2 flex items-center gap-3">
            <button data-testid="save-employee-button" type="submit" className="bg-[#4A5D4E] hover:bg-[#3B4A3E] text-white rounded-xl px-6 py-2.5 text-sm">Create</button>
            <button type="button" onClick={() => setCreating(false)} className="text-[#5C635E] text-sm">Cancel</button>
          </div>
        </form>
      )}

      <div className="card-warm p-3 flex flex-col sm:flex-row gap-2"><input data-testid="employees-search" aria-label="Search employees" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search name, username, phone or email" className="flex-1 bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2" /><select data-testid="employees-status-filter" aria-label="Employee status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2"><option value="all">All employees</option><option value="active">Active</option><option value="inactive">Inactive</option></select>{(search || statusFilter !== "all") && <button type="button" onClick={() => { setSearch(""); setStatusFilter("all"); }} className="border border-[#EAE6DF] rounded-lg px-4 py-2 text-[#C05746]">Clear</button>}</div>

      <div className="card-warm overflow-hidden">
        <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="bg-[#F4F1EA] text-[#5C635E] text-xs uppercase tracking-wider">
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Username</th>
              <th className="text-left p-3">Phone</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 ? (
              <tr><td colSpan="5" className="p-8 text-center text-[#8A938D]">No employees yet.</td></tr>
            ) : filteredItems.map((e) => (
              <tr key={e.id} data-testid={`employee-row-${e.id}`} className="border-t border-[#EAE6DF]">
                <td className="p-3 font-medium text-[#2D312E]">{e.name}</td>
                <td className="p-3 text-[#5C635E] font-mono text-xs">{e.username}</td>
                <td className="p-3 text-[#5C635E]">{e.phone || "—"}</td>
                <td className="p-3">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${e.active ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-[#F4F1EA] text-[#8A938D] border border-[#EAE6DF]"}`}>
                    {e.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="p-3 text-right">
                  <button data-testid={`edit-employee-${e.id}`} onClick={() => setEditing({ ...e })} className="text-sm text-[#4A5D4E] hover:underline mr-3">
                    Edit
                  </button>
                  <button data-testid={`reset-password-${e.id}`} onClick={() => { setPasswordTarget(e); setNewPassword(""); }} className="text-sm text-[#4A5D4E] hover:underline mr-3">
                    Reset password
                  </button>
                  <button data-testid={`toggle-employee-${e.id}`} onClick={() => toggleActive(e)} className="text-sm text-[#4A5D4E] hover:underline mr-3">
                    {e.active ? "Deactivate" : "Reactivate"}
                  </button>
                  <button data-testid={`delete-employee-${e.id}`} onClick={() => setDeleteTarget(e)} className="text-sm text-[#C05746] hover:underline">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {editing && (
        <div data-testid="edit-employee-modal" className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <form onSubmit={saveEdit} className="card-warm bg-white p-6 max-w-2xl w-full space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Edit {editing.username}</h3>
              <button type="button" onClick={() => setEditing(null)} className="text-[#8A938D]">×</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Full name" testid="edit-name" value={editing.name || ""} onChange={(v) => setEditing({ ...editing, name: v })} />
              <Input label="Phone" testid="edit-phone" value={editing.phone || ""} onChange={(v) => setEditing({ ...editing, phone: v })} />
              <Input label="Email" testid="edit-email" value={editing.email || ""} onChange={(v) => setEditing({ ...editing, email: v })} />
              <Input label="Basic Salary (₹/month)" testid="edit-basic-salary" type="number" value={editing.basic_salary ?? ""} onChange={(v) => setEditing({ ...editing, basic_salary: v })} />
              <Input label="Monthly Target (₹)" testid="edit-target" type="number" value={editing.monthly_target ?? ""} onChange={(v) => setEditing({ ...editing, monthly_target: v })} />
            </div>
              <Input label="Full Day Cutoff" testid="edit-cutoff" type="time" value={editing.full_day_cutoff_time || "10:30"} onChange={(v) => setEditing({ ...editing, full_day_cutoff_time: v })} />
            <div className="flex gap-3">
              <button data-testid="save-edit-employee" type="submit" disabled={savingEdit}
                className="bg-[#4A5D4E] hover:bg-[#3B4A3E] disabled:opacity-60 text-white rounded-xl px-6 py-2.5 text-sm">
                {savingEdit ? "Saving…" : "Save changes"}
              </button>
              <button type="button" onClick={() => setEditing(null)} className="text-[#5C635E] text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete employee?"
        message={`Permanently delete ${deleteTarget?.name}? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={() => { const emp = deleteTarget; setDeleteTarget(null); if (emp) permDelete(emp); }}
        onCancel={() => setDeleteTarget(null)}
      />
      {passwordTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="reset-password-dialog">
          <div className="card-warm bg-white p-6 max-w-md w-full space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Reset password</h3>
              <button type="button" onClick={() => setPasswordTarget(null)} className="text-[#8A938D]"><X className="h-5 w-5" /></button>
            </div>
            <div className="text-sm text-[#5C635E]">Enter a new password for {passwordTarget.name} ({passwordTarget.username}).</div>
            <Input label="New password" type="password" value={newPassword} onChange={(v) => setNewPassword(v)} />
            <div className="flex gap-3">
              <button type="button" onClick={() => resetPassword(passwordTarget)}
                className="bg-[#4A5D4E] hover:bg-[#3B4A3E] text-white rounded-xl px-5 py-2.5 text-sm">Save password</button>
              <button type="button" onClick={() => setPasswordTarget(null)} className="text-[#5C635E] text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
